import { Button, Heading } from '@assembly-js/design-system';
import { countries, getCountryData, TCountryCode } from 'countries-list';
import { Formik } from 'formik';
import { useEffect, useRef } from 'react';
import { toFormikValidationSchema } from 'zod-formik-adapter';

import { TextField } from 'src/components/TextField';
import useAddressValidation from 'src/features/crm/hooks/useAddressValidation';
import OptionsMenu from 'src/features/crm/properties/AddressInput/OptionsMenu';
import { CustomFieldValueData } from 'src/features/crm/types/type';
import CountryInput from 'src/legacy/components/Address/CountryInput';
import GoogleMapsAddressInput from 'src/legacy/components/Address/GoogleMapsAddressInput';
import { AddressCustomFieldValues } from 'src/store/clients/types';

const EMPTY_ADDRESS: Required<AddressCustomFieldValues> = {
  addressLine1: '',
  addressLine2: '',
  city: '',
  country: '',
  fullAddress: '',
  postalCode: '',
  region: '',
};

function isCountryCode(code: string): code is TCountryCode {
  return Object.keys(countries).includes(code);
}

interface AddressFormProps {
  fieldId: string;
  initialValues: Required<AddressCustomFieldValues>;
  onClosePopover: () => void;
  onUpdatePopoverPosition: () => void;
  onSaveValue: (property: CustomFieldValueData) => void;
}

export const AddressInputForm = ({
  fieldId,
  initialValues,
  onClosePopover: closePopover,
  onUpdatePopoverPosition,
  onSaveValue,
}: AddressFormProps) => {
  // save initial fullAddress value from before any editing happens
  const initialFullAddress = useRef(initialValues.fullAddress);
  const { isOnlyFullAddress, addressFieldSchema } = useAddressValidation(
    initialValues.addressLine1,
  );

  const handleSave = (values: Required<AddressCustomFieldValues>) => {
    onSaveValue({
      fieldId,
      value: {
        ...values,
        fullAddress: !isOnlyFullAddress({
          country: values.country,
          fullAddress: values.fullAddress,
        })
          ? [
              values.addressLine1,
              values.addressLine2,
              [values.city, values.region, values.postalCode]
                .filter((val) => val !== '')
                .join(', '),
              isCountryCode(values.country)
                ? getCountryData(values.country).name
                : '',
            ]
              .filter((val) => val !== '')
              .join('\n')
          : values.fullAddress,
      },
    });
    closePopover();
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={toFormikValidationSchema(addressFieldSchema)}
      validateOnChange
      validateOnBlur
      onSubmit={handleSave}
    >
      {({
        errors,
        handleBlur: formikHandleBlur,
        handleChange,
        handleSubmit,
        isSubmitting,
        setFieldValue,
        setValues,
        submitForm,
        touched,
        values,
      }) => {
        // forces the popover to reposition after the formik blur handler so that the added error text
        // doesn't cause the bottom of the popover to be cut off
        const handleBlur = (e: React.FocusEvent<any>) => {
          formikHandleBlur(e);
        };
        // forces the popover to reposition after country is changed or a submission is attempted so that the
        // popover content isn't cut off due to size changes
        useEffect(() => {
          onUpdatePopoverPosition();
        }, [values.country, isSubmitting]);

        const handleClearAddress = () => {
          setValues(EMPTY_ADDRESS);
          onSaveValue({
            fieldId,
            value: EMPTY_ADDRESS,
          });
          closePopover();
        };

        const isSelectedCountryUnitedKingdom = values.country === 'GB';
        return (
          <div className="p-3">
            <div className="flex items-start justify-between gap-2">
              <Heading size="lg" className="mb-1">
                Address
              </Heading>
              <OptionsMenu
                onClearAddress={handleClearAddress}
                // fullAddress is guaranteed to nonEmpty for a valid saved address
                isEmptyAddress={initialFullAddress.current === ''}
              />
            </div>
            <form onSubmit={handleSubmit}>
              <div className="flex flex-col gap-3">
                <CountryInput
                  label="Country"
                  autoFocus
                  country={values.country}
                  onCountryChange={(country) => {
                    // On country change, reset the address fields
                    setValues(EMPTY_ADDRESS);
                    if (country) {
                      setFieldValue('country', country);
                    }
                  }}
                  error={touched.country && errors.country !== undefined}
                  helperText={touched.country && errors.country}
                />

                {/* show other fields if either the country has been selected or a fullAddress already exists (possible from csv import) */}
                {(values.country !== '' || values.fullAddress !== '') && (
                  <>
                    {isOnlyFullAddress({
                      country: values.country,
                      fullAddress: values.fullAddress,
                    }) ? (
                      <TextField
                        name="fullAddress"
                        multiline
                        label="Address"
                        variant="outlined"
                        fullWidth
                        value={values.fullAddress}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        minRows={3}
                        autoFocus
                        error={
                          touched.fullAddress &&
                          errors.fullAddress !== undefined
                        }
                        helperText={touched.fullAddress && errors.fullAddress}
                        inputProps={{ 'data-1p-ignore': true }}
                      />
                    ) : (
                      <>
                        <GoogleMapsAddressInput
                          label="Address line 1"
                          addressValue={values.addressLine1}
                          autoFocus
                          onAddressValueChange={(e) => {
                            setFieldValue('addressLine1', e.target.value);
                          }}
                          country={values.country}
                          error={
                            touched.addressLine1 &&
                            errors.addressLine1 !== undefined
                          }
                          helperText={
                            touched.addressLine1 && errors.addressLine1
                          }
                          onAutocompleteChange={({
                            addressLine1,
                            addressLine2,
                            city,
                            state,
                            zipCode,
                          }) => {
                            // fires when an autocomplete option is selected
                            setValues({
                              ...values,
                              addressLine1:
                                addressLine1 !== ''
                                  ? `${addressLine1} ${addressLine2}`
                                  : addressLine2,
                              city,
                              region: !isSelectedCountryUnitedKingdom
                                ? state
                                : '',
                              postalCode: zipCode,
                            });
                          }}
                          onAutocompleteInputChange={(newValue, reason) => {
                            // fires when an autocomplete is typed in
                            if (reason === 'clear') {
                              setValues({
                                ...EMPTY_ADDRESS,
                                country: values.country,
                              });
                            } else if (reason !== 'reset') {
                              setFieldValue('addressLine1', newValue);
                            }
                          }}
                        />

                        <TextField
                          variant="outlined"
                          name="addressLine2"
                          value={values.addressLine2}
                          label="Address line 2"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          fullWidth
                          inputProps={{ 'data-1p-ignore': true }}
                        />

                        <TextField
                          variant="outlined"
                          name="city"
                          value={values.city}
                          label="City"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          fullWidth
                          error={touched.city && errors.city !== undefined}
                          helperText={touched.city && errors.city}
                          inputProps={{ 'data-1p-ignore': true }}
                        />

                        {!isSelectedCountryUnitedKingdom && (
                          <TextField
                            variant="outlined"
                            name="region"
                            value={values.region}
                            label="State"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            fullWidth
                            error={
                              touched.region && errors.region !== undefined
                            }
                            helperText={touched.region && errors.region}
                            inputProps={{ 'data-1p-ignore': true }}
                          />
                        )}

                        <TextField
                          variant="outlined"
                          name="postalCode"
                          value={values.postalCode}
                          label={
                            values.country === 'US' ? 'Zip code' : 'Postal code'
                          }
                          onChange={handleChange}
                          onBlur={handleBlur}
                          fullWidth
                          error={
                            touched.postalCode &&
                            errors.postalCode !== undefined
                          }
                          helperText={touched.postalCode && errors.postalCode}
                          inputProps={{ 'data-1p-ignore': true }}
                        />
                      </>
                    )}
                    <Button label="Save" onClick={submitForm} />
                  </>
                )}
              </div>
            </form>
          </div>
        );
      }}
    </Formik>
  );
};
